// Legacy: `<ul class="extr-socials">` block duplicated verbatim across
// architect-template, modern-template, classic-template and minimal-template.
// Order and field names match the legacy markup exactly.

import type { PortfolioRenderData } from '../../types';

export type SocialLinkDefinition = {
  key: string;
  urlField: keyof PortfolioRenderData;
  hiddenField: keyof PortfolioRenderData;
  label: string;
  icon: string;
};

export const SOCIAL_LINK_DEFINITIONS: SocialLinkDefinition[] = [
  { key: 'twitter', urlField: 'twitter_url', hiddenField: 'is_twitter_url_hidden', label: 'Twitter', icon: 'fab fa-twitter' },
  { key: 'instagram', urlField: 'instagram_url', hiddenField: 'is_instagram_url_hidden', label: 'Instagram', icon: 'fab fa-instagram' },
  { key: 'facebook', urlField: 'facebook_url', hiddenField: 'is_facebook_url_hidden', label: 'Facebook', icon: 'fab fa-facebook-f' },
  { key: 'linkedin', urlField: 'linkedin_url', hiddenField: 'is_linkedin_url_hidden', label: 'LinkedIn', icon: 'fab fa-linkedin-in' },
  { key: 'website', urlField: 'personal_website_url', hiddenField: 'is_personal_website_url_hidden', label: 'Website', icon: 'fa-solid fa-globe' },
  { key: 'youtube', urlField: 'youtube_url', hiddenField: 'is_youtube_url_hidden', label: 'YouTube', icon: 'fab fa-youtube' },
  { key: 'vimeo', urlField: 'vimeo_url', hiddenField: 'is_vimeo_url_hidden', label: 'Vimeo', icon: 'fab fa-vimeo-v' },
  { key: 'dribble', urlField: 'dribble_url', hiddenField: 'is_dribble_url_hidden', label: 'Dribbble', icon: 'fab fa-dribbble' },
  { key: 'github', urlField: 'github_url', hiddenField: 'is_github_url_hidden', label: 'GitHub', icon: 'fab fa-github' },
  { key: 'carbonmade', urlField: 'carbonmade_url', hiddenField: 'is_carbonmade_url_hidden', label: 'CarbonMade', icon: 'fa-solid fa-pen-nib' },
  { key: 'pinterest', urlField: 'pinterest_url', hiddenField: 'is_pinterest_url_hidden', label: 'Pinterest', icon: 'fab fa-pinterest-p' },
  { key: 'soundcloud', urlField: 'soundcloud_url', hiddenField: 'is_soundcloud_url_hidden', label: 'SoundCloud', icon: 'fab fa-soundcloud' },
  { key: 'tumblr', urlField: 'tumblr_url', hiddenField: 'is_tumblr_url_hidden', label: 'Tumblr', icon: 'fab fa-tumblr' },
];

export type VisibleSocialLink = {
  key: string;
  href: string;
  label: string;
  icon: string;
};

/**
 * Legacy condition per link: `portfolioData.X_url && portfolioData.is_X_url_hidden == false`.
 * A link only renders when a URL is present AND the hidden flag is explicitly `false`.
 */
export function getVisibleSocialLinks(
  data: PortfolioRenderData | null | undefined,
): VisibleSocialLink[] {
  if (!data) {
    return [];
  }
  return SOCIAL_LINK_DEFINITIONS.reduce<VisibleSocialLink[]>((acc, def) => {
    const href = data[def.urlField];
    const hidden = data[def.hiddenField];
    if (typeof href === 'string' && href && hidden === false) {
      acc.push({ key: def.key, href, label: def.label, icon: def.icon });
    }
    return acc;
  }, []);
}

export type SocialLinksNavProps = {
  data: PortfolioRenderData | null | undefined;
  className?: string;
  itemClassName?: string;
};

/** Shared `<ul class="extr-socials">` social icon nav used by every template. */
export function SocialLinksNav({ data, className = 'extr-socials', itemClassName }: SocialLinksNavProps) {
  const links = getVisibleSocialLinks(data);
  if (links.length === 0) {
    return null;
  }
  return (
    <ul className={className}>
      {links.map((link) => (
        <li key={link.key} className={itemClassName}>
          <a
            title={link.label}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={link.label}
          >
            <i className={link.icon} aria-hidden="true" />
          </a>
        </li>
      ))}
    </ul>
  );
}
